import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";

import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";

import getQueryKeys from "./getQueryKeys";

import type { Lead } from "../types";

export default function useUpdateLead() {
  const queryClient = useQueryClient();

  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { leadsKeys } = getQueryKeys(workspaceId);

  //this function is used to update leads (leads returned in the leads page)
  async function updateAllLeads(updateFunction: (lead: Lead) => void) {
    if (queryClient.getQueriesData({ queryKey: leadsKeys.list() })) {
      queryClient.setQueriesData<Lead[] | InfiniteData<Lead[]>>(
        { queryKey: leadsKeys.list() },
        (prevLeads) => {
          if (prevLeads) {
            //infinite query is used for drip leads
            if ("pages" in prevLeads) {
              return produce(prevLeads, (draftState) => {
                draftState.pages.forEach((leads) => {
                  for (let i = 0; i < leads.length; i += 1) {
                    updateFunction(leads[i]);
                  }
                });
              });
            }
            return produce(prevLeads, (draftState) => {
              for (let i = 0; i < draftState.length; i += 1) {
                updateFunction(draftState[i]);
              }
            });
          }
          return undefined;
        },
      );
    }
  }
  async function updateLead(
    leadId: string,
    updateFunction: (lead: Lead) => void,
  ) {
    if (queryClient.getQueriesData({ queryKey: leadsKeys.list() })) {
      await queryClient.cancelQueries({ queryKey: leadsKeys.list() });
      queryClient.setQueriesData<Lead[] | InfiniteData<Lead[]>>(
        { queryKey: leadsKeys.list() },
        (prevLeads) => {
          if (prevLeads) {
            //infinite query is used for drip leads
            if ("pages" in prevLeads) {
              return {
                ...prevLeads,
                pages: prevLeads.pages.map((leads) =>
                  produce(leads, (draftState) => {
                    for (let i = 0; i < draftState.length; i += 1) {
                      if (leadId === draftState[i].id) {
                        updateFunction(draftState[i]);
                        return;
                      }
                    }
                  }),
                ),
              };
            }
            return produce(prevLeads, (draftState) => {
              for (let i = 0; i < draftState.length; i += 1) {
                if (leadId === draftState[i].id) {
                  updateFunction(draftState[i]);
                  return;
                }
              }
            });
          }
          return undefined;
        },
      );
    }
  }
  return { updateLead, updateAllLeads };
}
